/**
 * PredictionToggle Component
 *
 * A toggle for selecting between mutually exclusive prediction options.
 * Used for carrier prediction (approved/needs_improvement/rejected) in admin feedback.
 *
 * Behavior:
 * - Clicking an unselected option selects it
 * - Clicking the selected option deselects it (returns to null)
 * - Only one option can be selected at a time
 */

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { Check, X, AlertCircle } from "lucide-react";

export type PredictionValue = "approved" | "needs_improvement" | "rejected" | null;

interface PredictionToggleProps {
  value: PredictionValue;
  onChange: (value: PredictionValue) => void;
  disabled?: boolean;
  className?: string;
}

export function PredictionToggle({
  value,
  onChange,
  disabled,
  className,
}: PredictionToggleProps) {
  const handleClick = (clickedValue: "approved" | "needs_improvement" | "rejected") => {
    // Toggle off if clicking the already-selected option
    onChange(value === clickedValue ? null : clickedValue);
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => handleClick("approved")}
        className={cn(
          "flex-1 gap-2 transition-colors text-xs px-2",
          value === "approved" &&
            "bg-green-100 border-green-500 text-green-700 hover:bg-green-200 hover:text-green-800 dark:bg-green-950 dark:border-green-600 dark:text-green-300 dark:hover:bg-green-900"
        )}
      >
        <Check className="h-4 w-4 shrink-0" />
        Approved
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => handleClick("needs_improvement")}
        className={cn(
          "flex-1 gap-2 transition-colors text-xs px-2",
          value === "needs_improvement" &&
            "bg-amber-100 border-amber-500 text-amber-700 hover:bg-amber-200 hover:text-amber-800 dark:bg-amber-950 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900"
        )}
      >
        <AlertCircle className="h-4 w-4 shrink-0" />
        Approved, needs improvement
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => handleClick("rejected")}
        className={cn(
          "flex-1 gap-2 transition-colors text-xs px-2",
          value === "rejected" &&
            "bg-red-100 border-red-500 text-red-700 hover:bg-red-200 hover:text-red-800 dark:bg-red-950 dark:border-red-600 dark:text-red-300 dark:hover:bg-red-900"
        )}
      >
        <X className="h-4 w-4 shrink-0" />
        Rejected
      </Button>
    </div>
  );
}
